'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { v4 as uuid } from 'uuid'
import { captureUtmParams } from '@/lib/utm-tracking'

const VISITOR_COOKIE_NAME = 'iperform_visitor_id'
const VISITOR_STORAGE_KEY = 'visitorId'
const LAST_REFRESH_KEY = 'visitorLastRefresh'
// Refresh visitor data at most once per hour to avoid excessive API calls
const REFRESH_INTERVAL_MS = 60 * 60 * 1000

/**
 * Get visitor ID from cookie (client-side)
 */
function getVisitorIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null
  
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === VISITOR_COOKIE_NAME) {
      return value
    }
  }
  return null
}

/**
 * Get visitor ID from localStorage (fallback)
 */
function getVisitorIdFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(VISITOR_STORAGE_KEY)
}

/**
 * Save visitor ID to localStorage (fallback)
 */
function saveVisitorIdToStorage(visitorId: string): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(VISITOR_STORAGE_KEY, visitorId)
}

/**
 * Check if we should refresh visitor data (rate limiting)
 */
function shouldRefreshVisitor(): boolean {
  if (typeof localStorage === 'undefined') return true
  
  const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY)
  if (!lastRefresh) return true
  
  const lastRefreshTime = parseInt(lastRefresh, 10)
  return Date.now() - lastRefreshTime > REFRESH_INTERVAL_MS
}

/**
 * Mark visitor as refreshed
 */
function markVisitorRefreshed(): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString())
}

/**
 * Hook to get and manage the persistent visitor ID.
 * 
 * Priority order:
 * 1. Cookie (set by server, 10 year expiration)
 * 2. localStorage (fallback)
 * 3. Generate new UUID
 * 
 * The hook will:
 * - Return an immediate ID (from cookie or localStorage) for instant use
 * - Call the API to ensure the cookie is set/refreshed and visitor is persisted to DB
 * - Migrate localStorage IDs to cookies if needed
 * - Update lastSeen in the database on each visit (rate-limited to once per hour)
 * - Track visitors across multiple events/artists for profile building
 * - ALWAYS send UTM params to enable backfill for visitors created without attribution
 */
export function useVisitor() {
  // Get initial ID synchronously for immediate use
  // On the server, use empty string to avoid hydration mismatch (random uuid would differ)
  // On the client, read from cookie/localStorage immediately for instant availability
  const [visitorId, setVisitorId] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return '' // SSR: empty string avoids hydration mismatch from random uuid
    }

    // Try cookie first, then localStorage
    const cookieId = getVisitorIdFromCookie()
    if (cookieId) {
      // Also save to localStorage as backup
      saveVisitorIdToStorage(cookieId)
      return cookieId
    }

    const storageId = getVisitorIdFromStorage()
    if (storageId) {
      return storageId
    }

    // Generate new ID
    const newId = uuid()
    saveVisitorIdToStorage(newId)
    return newId
  })

  const [isLoading, setIsLoading] = useState(true)
  const [isNewVisitor, setIsNewVisitor] = useState(false)
  const hasInitialized = useRef(false)

  // Call API to ensure cookie is set, visitor is persisted, and lastSeen is updated
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (hasInitialized.current) return
    hasInitialized.current = true

    async function ensureCookie() {
      try {
        // Check if we have a localStorage ID but no cookie (migration case)
        const cookieId = getVisitorIdFromCookie()
        const storageId = getVisitorIdFromStorage()
        
        // Capture UTM params FIRST - we need these for attribution
        // This captures from URL params or infers from page context (artist/event pages)
        const utmParams = captureUtmParams()
        
        // Determine if we need to call the API
        const needsMigration = !cookieId && storageId
        const needsRefresh = shouldRefreshVisitor()
        // ALWAYS call API if we have UTM params - enables backfill for visitors
        // that were created by tRPC handlers without attribution data
        const hasUtmParams = utmParams && utmParams.utmSource
        
        if (needsMigration) {
          // Migrate localStorage ID to cookie and persist to DB
          console.log('[useVisitor] Migrating localStorage visitor to cookie', { storageId, utmParams })
          const response = await fetch('/api/visitor', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              visitorId: storageId,
              utmParams,
            }),
          })
          
          if (response.ok) {
            const data = await response.json()
            setVisitorId(data.visitorId)
            saveVisitorIdToStorage(data.visitorId)
            markVisitorRefreshed()
          }
        } else if (!cookieId || needsRefresh || hasUtmParams) {
          // Normal flow: get or create visitor via API
          // This also persists to DB, updates lastSeen, and backfills UTM if needed
          const response = await fetch('/api/visitor', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ utmParams }),
          })
          
          if (response.ok) {
            const data = await response.json()
            setVisitorId(data.visitorId)
            setIsNewVisitor(data.isNew)
            saveVisitorIdToStorage(data.visitorId)
            markVisitorRefreshed()
          }
        } else {
          // No need to call API - visitor exists and no UTM to backfill
          setIsLoading(false)
          return
        }
      } catch (error) {
        // API failed, but we already have a visitorId from initialization
        console.warn('[useVisitor] Failed to sync visitor cookie:', error)
      } finally {
        setIsLoading(false)
      }
    }

    ensureCookie()
  }, [])

  // Refresh the cookie and update lastSeen (call on important actions)
  const refreshCookie = useCallback(async () => {
    try {
      const response = await fetch('/api/visitor')
      if (response.ok) {
        const data = await response.json()
        setVisitorId(data.visitorId)
        saveVisitorIdToStorage(data.visitorId)
        markVisitorRefreshed()
      }
    } catch (error) {
      console.warn('[useVisitor] Failed to refresh visitor cookie:', error)
    }
  }, [])

  return {
    visitorId,
    isLoading,
    isNewVisitor,
    refreshCookie,
  }
}

/**
 * Simple synchronous function to get visitor ID.
 * Use this when you need the ID immediately without waiting for the hook.
 * Falls back to generating a new ID if none exists.
 * 
 * Note: This function does NOT persist to the database or update lastSeen.
 * For that, use the useVisitor hook or call /api/visitor directly.
 */
export function getVisitorId(): string {
  if (typeof window === 'undefined') {
    return uuid()
  }
  
  const cookieId = getVisitorIdFromCookie()
  if (cookieId) return cookieId
  
  const storageId = getVisitorIdFromStorage()
  if (storageId) return storageId
  
  const newId = uuid()
  saveVisitorIdToStorage(newId)
  return newId
}

/**
 * Async function to ensure visitor is persisted to database with UTM params.
 * Call this when you need to guarantee the visitor exists in DB.
 * Returns the visitor ID.
 */
export async function ensureVisitorPersisted(): Promise<string> {
  // Capture UTM params for attribution
  const utmParams = captureUtmParams()
  
  const response = await fetch('/api/visitor', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ utmParams }),
  })
  
  if (response.ok) {
    const data = await response.json()
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(VISITOR_STORAGE_KEY, data.visitorId)
      localStorage.setItem(LAST_REFRESH_KEY, Date.now().toString())
    }
    return data.visitorId
  }
  // Fallback to local ID if API fails
  return getVisitorId()
}
